import { useState, type FormEvent, type ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { Loader2 } from 'lucide-react';
import { LIMITS, type ControlMode, type CreateRoomInput } from '@vybe/shared';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogTrigger } from '@/components/ui/dialog';
import { Input, Label } from '@/components/ui/input';
import { Segmented } from '@/components/ui/segmented';
import { SwitchRow } from '@/components/ui/switch';
import { useI18n } from '@/locales';
import { api, ApiError } from '@/services/api';
import { useSessionStore } from '@/stores/session.store';
import { roomPath } from '../invitations/invite';
import { NameForm } from './NameForm';

export function CreateRoomDialog({ trigger }: { trigger: ReactNode }) {
  const { m } = useI18n();
  const [open, setOpen] = useState(false);
  const status = useSessionStore((s) => s.status);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      {status === 'ready' ? (
        <DialogContent
          title={m.create.title}
          description={m.create.description}
          closeLabel={m.common.close}
        >
          <CreateRoomForm />
        </DialogContent>
      ) : (
        <DialogContent
          title={m.name.title}
          description={m.name.description}
          closeLabel={m.common.close}
        >
          <NameForm />
        </DialogContent>
      )}
    </Dialog>
  );
}

function CreateRoomForm() {
  const { m } = useI18n();
  const navigate = useNavigate();
  const token = useSessionStore((s) => s.token);
  const invalidate = useSessionStore((s) => s.invalidate);
  const [form, setForm] = useState<CreateRoomInput>({
    name: '',
    isPrivate: true,
    maxParticipants: 4,
    controlMode: 'host',
    allowVoice: true,
    allowCamera: true,
  });
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const patch = (value: Partial<CreateRoomInput>) => setForm((f) => ({ ...f, ...value }));

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!token) return;
    setBusy(true);
    setFailed(false);
    try {
      const room = await api.createRoom(token, {
        ...form,
        name: form.name.trim() || m.create.defaultName,
      });
      void navigate(roomPath(room.roomId, room.inviteToken));
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) invalidate();
      setFailed(true);
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-5">
      <div className="space-y-2">
        <Label htmlFor="room-name">{m.create.name}</Label>
        <Input
          id="room-name"
          autoFocus
          maxLength={LIMITS.roomNameMax}
          placeholder={m.create.namePlaceholder}
          value={form.name}
          onChange={(e) => patch({ name: e.target.value })}
        />
      </div>

      <div className="space-y-2">
        <span className="text-sm font-medium text-fg-muted">{m.create.control}</span>
        <Segmented<ControlMode>
          label={m.create.control}
          value={form.controlMode}
          onChange={(controlMode) => patch({ controlMode })}
          options={[
            { value: 'host', label: m.create.controlHost },
            { value: 'shared', label: m.create.controlShared },
          ]}
        />
      </div>

      <div className="space-y-2">
        <span className="text-sm font-medium text-fg-muted">{m.create.maxParticipants}</span>
        <Segmented<number>
          label={m.create.maxParticipants}
          value={form.maxParticipants}
          onChange={(maxParticipants) => patch({ maxParticipants })}
          options={[2, 4, 6, 8].map((n) => ({ value: n, label: String(n) }))}
        />
      </div>

      <div className="divide-y divide-line rounded-xl bg-ink-900 px-4 ring-1 ring-line">
        <SwitchRow
          label={m.create.private}
          hint={m.create.privateHint}
          checked={form.isPrivate}
          onCheckedChange={(isPrivate) => patch({ isPrivate })}
        />
        <SwitchRow
          label={m.create.voice}
          checked={form.allowVoice}
          onCheckedChange={(allowVoice) => patch({ allowVoice })}
        />
        <SwitchRow
          label={m.create.camera}
          checked={form.allowCamera}
          onCheckedChange={(allowCamera) => patch({ allowCamera })}
        />
      </div>

      {failed ? (
        <p role="alert" className="text-sm text-danger">
          {m.create.failed}
        </p>
      ) : null}

      <Button type="submit" variant="primary" size="lg" className="w-full" disabled={busy}>
        {busy ? <Loader2 className="animate-spin" /> : null}
        {m.create.submit}
      </Button>
    </form>
  );
}
